// OrganismSpinner — the loading state, drawn from the canonical organism mark.
// ONLY the outer petals rotate; the pill-shaped centre stays erect (it never
// turns), masked out of the spinning petals so it reads as a still, upright eye
// while the bloom turns around it.
import { useId } from "react";
import { ORGANISM, BRAND } from "../../lib/brand/marks";

type Tone = "current" | "solid" | "gradient";

// Split the canonical Figma organism into its petal silhouette + centre pill (the
// path is one even-odd shape: outer subpath then the inner hole subpath).
function canonicalParts() {
  const i = ORGANISM.path.search(/Z(?=\s*M)/);
  if (i < 0) return { viewBox: ORGANISM.viewBox, outer: ORGANISM.path, inner: "" };
  return { viewBox: ORGANISM.viewBox, outer: ORGANISM.path.slice(0, i + 1), inner: ORGANISM.path.slice(i + 1) };
}

export default function OrganismSpinner({
  size = 24,
  tone = "current",
  title,
  className,
  style,
  spin = true,
}: {
  size?: string | number;
  tone?: Tone;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  /** false → a STILL brand mark (petals don't turn) — e.g. the footer lockup, so
   *  it reads as a consistent logo, not a perpetual loader. */
  spin?: boolean;
}) {
  const id = useId().replace(/[:]/g, "");
  const { viewBox, outer, inner } = canonicalParts();
  const gradId = `sp-grad-${id}`;
  const maskId = `sp-mask-${id}`;
  const fill = tone === "gradient" ? `url(#${gradId})` : tone === "solid" ? BRAND.blue : "currentColor";
  const [, , w, h] = viewBox.split(" ").map(Number);
  return (
    <svg
      viewBox={viewBox}
      width={size}
      height={size}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
      // overflow:visible so a rotating petal that swings past the tight Figma
      // viewBox is NEVER sheared at the SVG viewport (the clipping John flagged on
      // the spinner + footer mark). Paired with the oversized mask rect below.
      style={{ display: "inline-block", verticalAlign: "middle", fill, flexShrink: 0, overflow: "visible", ...style }}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        {tone === "gradient" && (
          <linearGradient id={gradId} x1={w / 2} y1="0" x2={w / 2} y2={h} gradientUnits="userSpaceOnUse">
            <stop stopColor={BRAND.organismGrad[0]} />
            <stop offset="1" stopColor={BRAND.organismGrad[1]} />
          </linearGradient>
        )}
        {inner && (
          // the mask's white field must extend WELL beyond the viewBox — the
          // canonical rect (0,0,w,h) masked away any petal that rotated past the
          // box edge, which is exactly the clip. Span the full rotation envelope.
          <mask id={maskId}>
            <rect x={-w} y={-h} width={w * 3} height={h * 3} fill="#fff" />
            <path d={inner} fill="#000" />
          </mask>
        )}
      </defs>
      <g mask={inner ? `url(#${maskId})` : undefined}>
        <g className="io-spin__petals" style={spin ? undefined : { animation: "none" }}>
          <path d={outer} fillRule="evenodd" />
        </g>
      </g>
    </svg>
  );
}
